import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeEs from '@angular/common/locales/es';
import { ApplicationConfig, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { provideRouter } from '@angular/router';
import { provideTranslateService, TranslateLoader, TranslateService } from '@ngx-translate/core';
import player from 'lottie-web';
import { provideLottieOptions } from 'ngx-lottie';
import { firstValueFrom, forkJoin, map, Observable } from 'rxjs';
import { routes } from './app.routes';
import { LanguageService } from './core/services/language.service';
import { UserAgentInterceptor } from './core/interceptors/user-agent.interceptor';
import { StorageRetryInterceptor } from './core/interceptors/storage-retry.interceptor';
import { LanguageInterceptor } from './core/interceptors/language.interceptor';
import { ConsoleCaptureService } from './shared/services/console-capture.service';
import { DevToolsProtectionService } from './core/services/devtools-protection.service';

class MultiHttpLoader implements TranslateLoader {
	constructor(
		private http: HttpClient,
		public resources: string[],
	) {}

	getTranslation(lang: string): Observable<any> {
		const requests = this.resources.map((path) => this.http.get(`${path}${lang}.json`));
		return forkJoin(requests).pipe(map((response) => Object.assign({}, ...response)));
	}
}

registerLocaleData(localeEs);
const httpLoaderFactory: (http: HttpClient) => MultiHttpLoader = (http: HttpClient) =>
	new MultiHttpLoader(http, [
		'/i18n/', // Ruta de la app
		'/assets/i18n/', // Ruta de la librería
	]);

export const appConfig: ApplicationConfig = {
	providers: [
		provideZoneChangeDetection({ eventCoalescing: true }),
		provideRouter(routes),
		provideHttpClient(withInterceptorsFromDi()),
		{ provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: UserAgentInterceptor, multi: true },
		// NIF-646: reintento automático de peticiones journey ante KO 2099 + storage_retry.
		{ provide: HTTP_INTERCEPTORS, useClass: StorageRetryInterceptor, multi: true },
		provideTranslateService({
			lang: 'es',
			fallbackLang: 'es',
			useDefaultLang: true,
			loader: {
				provide: TranslateLoader,
				useFactory: httpLoaderFactory,
				deps: [HttpClient],
			},
		}),
		// Esperamos a que se carguen los idiomas antes de iniciar la app
		provideAppInitializer(() => {
			const translate = inject(TranslateService);
			const language = inject(LanguageService);
			inject(ConsoleCaptureService).install();
			inject(DevToolsProtectionService).activate();
			// Sin id de proceso: no leer storage legacy compartido entre procesos
			language.bootstrapLanguage();
			// onLangChange del AudioService hará la recarga de audios
			return firstValueFrom(translate.use(language.getLanguage()));
		}),
		provideNativeDateAdapter(),
		{ provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
		// Lottie
		provideLottieOptions({
			player: () => player,
		}),
	],
};
